import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Lock, RefreshCw, ArrowLeft, Mail, Phone, Calendar, Users,
  MessageSquare, LogOut, Inbox, Loader2, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Input } from './ui/input';
import { CONTACT } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'lbv_admin_token';

export default function AdminBookings() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [pwd, setPwd] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBookings = useCallback(async (t) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { 'X-Admin-Token': t } });
      setBookings(res.data);
      setAuthed(true);
      sessionStorage.setItem(TOKEN_KEY, t);
      setToken(t);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setError('Password non corretta.');
        setAuthed(false);
        sessionStorage.removeItem(TOKEN_KEY);
      } else {
        setError('Errore di connessione. Riprova.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchBookings(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = (e) => {
    e.preventDefault();
    if (pwd.trim()) fetchBookings(pwd.trim());
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setToken('');
    setPwd('');
    setBookings([]);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('it-IT', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // ---- Login screen ----
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#EFE5D4] flex items-center justify-center px-5">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <p className="font-display text-4xl text-[#2A2420]">la Bellavita</p>
            <p className="font-body text-[11px] tracking-[0.4em] uppercase text-[#B5603A] mt-1">rooms</p>
          </div>
          <form onSubmit={onLogin} className="bg-[#F6EFE3] rounded-[2px] shadow-[0_25px_55px_-30px_rgba(42,36,32,0.5)] p-8">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-10 h-10 rounded-full bg-[#B5603A]/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#B5603A]" />
              </span>
              <h1 className="font-display text-2xl text-[#2A2420]">Area riservata</h1>
            </div>
            <label className="font-body text-sm text-[#6E655B] mb-2 block">Password</label>
            <Input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Inserisci la password"
              className="bg-[#F6EFE3] border-[#E7DAC4] focus-visible:ring-[#B5603A] text-[#2A2420] font-body h-12 rounded-[2px]"
              autoFocus
            />
            {error && <p className="font-body text-sm text-[#B5603A] mt-3">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 font-body tracking-wide px-6 py-3.5 rounded-full bg-[#B5603A] text-white hover:bg-[#8F4A2C] transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Accedi
            </button>
            <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 font-body text-sm text-[#6E655B] hover:text-[#B5603A] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Torna al sito
            </Link>
          </form>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="min-h-screen bg-[#EFE5D4]">
      <header className="bg-[#2A2420] text-[#E7DAC4]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-5 flex items-center justify-between">
          <div>
            <p className="font-display text-2xl text-white leading-none">Prenotazioni</p>
            <p className="font-body text-[10px] tracking-[0.35em] uppercase text-[#D69A4C] mt-1">la Bellavita rooms</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchBookings(token)}
              className="inline-flex items-center gap-2 font-body text-sm px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 font-body text-sm px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <Inbox className="w-5 h-5 text-[#B5603A]" />
          <p className="font-body text-[#6E655B]">
            {bookings.length === 0
              ? 'Nessuna richiesta ricevuta al momento.'
              : `${bookings.length} richieste ricevute`}
          </p>
        </div>

        {bookings.length === 0 ? (
          <div className="bg-[#F6EFE3] rounded-[2px] p-12 text-center">
            <Inbox className="w-12 h-12 text-[#D69A4C] mx-auto mb-4" />
            <p className="font-display text-2xl text-[#2A2420] mb-2">Ancora nessuna prenotazione</p>
            <p className="font-body text-[#6E655B]">Le richieste inviate dal modulo del sito appariranno qui.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {bookings.map((b) => (
              <article key={b.id} className="bg-[#F6EFE3] rounded-[2px] shadow-[0_15px_35px_-25px_rgba(42,36,32,0.5)] overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E7DAC4] flex items-center justify-between gap-3">
                  <h2 className="font-display text-2xl text-[#2A2420]">{b.name}</h2>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="font-body text-[11px] text-[#6E655B]">{fmtDate(b.created_at)}</span>
                    {b.email_sent ? (
                      <span className="inline-flex items-center gap-1 font-body text-[11px] text-[#3f7d52] bg-[#e4efe4] px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Email inviata
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-body text-[11px] text-[#9a6b2f] bg-[#f3e6cf] px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" /> Solo salvata
                      </span>
                    )}
                  </div>
                </div>
                <div className="px-6 py-5 space-y-3 font-body text-[15px] text-[#2A2420]">
                  <a href={`mailto:${b.email}`} className="flex items-center gap-3 hover:text-[#B5603A] transition-colors">
                    <Mail className="w-4 h-4 text-[#B5603A] shrink-0" /> {b.email}
                  </a>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#B5603A] shrink-0" /> {b.dates || '\u2014'}
                    </span>
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#B5603A] shrink-0" /> {b.guests || '\u2014'} ospiti
                    </span>
                  </div>
                  {b.message && (
                    <div className="flex items-start gap-3 pt-1">
                      <MessageSquare className="w-4 h-4 text-[#B5603A] shrink-0 mt-1" />
                      <p className="text-[#6E655B] leading-relaxed">{b.message}</p>
                    </div>
                  )}
                </div>
                <div className="px-6 py-4 bg-[#EFE5D4]/60 flex gap-3">
                  <a
                    href={`mailto:${b.email}?subject=La Bellavita Rooms - Risposta alla tua richiesta`}
                    className="inline-flex items-center gap-2 font-body text-sm px-5 py-2 rounded-full bg-[#B5603A] text-white hover:bg-[#8F4A2C] transition-colors"
                  >
                    <Mail className="w-4 h-4" /> Rispondi
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-10">
          <Link to="/" className="inline-flex items-center gap-1.5 font-body text-sm text-[#6E655B] hover:text-[#B5603A] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
        </div>
      </main>
    </div>
  );
}
